import {css, StyleSheet} from 'aphrodite';
import stringify from 'json-stable-stringify';
import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  RegistrySupportedType,
  SequenceSchemaType,
  TagSchemaSupportedFolderItemType,
} from 'constants/RegistryConstants';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getTagSchemaTypeName} from 'tag-schema/util/TagSchemaUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

// More strict version of SchemaInterfaceQuery_schemaInterface_SchemaInterfaceNode_baseBioentityType with enums instead of strings
export interface BaseBioentityType {
  folderItemType: TagSchemaSupportedFolderItemType;
  sequenceType: TagSchemaSequenceSchemaType | null;
}

export type OptionalBaseBioentityType = BaseBioentityType | null;

const generateOptions = (): Array<OptionalBaseBioentityType> => [
  null,
  // @ts-expect-error Falsey values are filtered out, but not recognized by typeguard.
  ...[
    {
      folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM as RegistrySupportedType,
      sequenceType: null,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE as RegistrySupportedType,
      sequenceType: SequenceSchemaType.DNA_SEQUENCE,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE as RegistrySupportedType,
      sequenceType: SequenceSchemaType.OLIGO,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE as RegistrySupportedType,
      sequenceType: SequenceSchemaType.RNA_SEQUENCE,
    },
    {
      folderItemType: ModelTypeConstants.SEQUENCE as RegistrySupportedType,
      sequenceType: SequenceSchemaType.RNA_OLIGO,
    },
    {folderItemType: ModelTypeConstants.PROTEIN as RegistrySupportedType, sequenceType: null},
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && {
      folderItemType: ModelTypeConstants.MOLECULE as RegistrySupportedType,
      sequenceType: null,
    },
    {
      folderItemType: ModelTypeConstants.MIXTURE as RegistrySupportedType,
      sequenceType: null,
    },
  ].filter((t) => Boolean(t)),
];

export const bioentityTypeToHumanName = (baseType: OptionalBaseBioentityType): String => {
  if (baseType == null) {
    return 'None';
  }
  return getTagSchemaTypeName({
    schema: {folderItemType: baseType.folderItemType, sequenceType: baseType.sequenceType},
  });
};

export interface SchemaInterfaceBaseBioentityTypeDropdownProps {
  bioentityType: OptionalBaseBioentityType;
  onBioentityTypeChanged: (bioentityType: OptionalBaseBioentityType) => void;
  canSetBaseBioentityType: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function SchemaInterfaceBaseBioentityTypeDropdown(
  props: SchemaInterfaceBaseBioentityTypeDropdownProps
): JSX.Element {
  const bioentityBaseTypeOptions = generateOptions();
  return (
    <InputWrapper
      id={_.uniqueId('baseBioentityType')}
      label="Entity Type"
      labelStyle={FieldLabel.STYLES.LABEL}
      isRequired={false}
    >
      <SelectDropdown
        className={css(styles.dropdown)}
        displayName={bioentityTypeToHumanName(props.bioentityType)}
        disabled={!props.canSetBaseBioentityType}
        dataTestAttrs={props.dataTestAttrs}
        menu={
          <Menu
            value={props.bioentityType}
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            scrollable={true}
          >
            {bioentityBaseTypeOptions.map((v) => {
              const key = stringify(v);
              return (
                <Option
                  key={key}
                  value={key}
                  onSelect={() => {
                    props.onBioentityTypeChanged(v);
                  }}
                >
                  {bioentityTypeToHumanName(v)}
                </Option>
              );
            })}
          </Menu>
        }
      />
    </InputWrapper>
  );
}

const styles = StyleSheet.create({
  dropdown: {
    flex: 1,
  },
});
